import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, } from "react-redux";
import { fetchDashboardData } from "../../rtk/thunks/dashBoardThunk.js";
import { useNavigate } from "react-router-dom";
import useResponsive, { BREAKPOINTS } from "../../hooks/useResponsive";


// SVG Icons (same as original)
const DashboardIcon = ({ size = 24, alt = "Dashboard" }) => (
  <img src="/assets/dashboardIcon.png" width={size} height={size} alt={alt} />
);

const BookIcon = ({ size = 24, alt = "Book" }) => (
  <img src="/assets/readScore.png" width={size} height={size} alt={alt} />
);

const CalendarIcon = ({ size = 24, alt = "Calendar" }) => (
  <img src="/assets/3.png" width={size} height={size} alt={alt} />
);
const TrophyIcon = ({ size = 24, alt = "Trophy" }) => (
  <img src="/assets/4.png" width={size} height={size} alt={alt} />
);
const DocumentIcon = ({ size = 24, alt = "Document" }) => (
  <img src="/assets/document.png" width={size} height={size} alt={alt} />
);
const ReadyIcon = ({ size = 24, alt = "Ready" }) => (
  <img src="/assets/readytest.png" width={size} height={size} alt={alt} />
);
const WarningTriangleIcon = ({ size = 24, alt = "Warning" }) => (
  <img src="/assets/warning.png" width={size} height={size} alt={alt} />
);
const GreenBookIcon = ({ size = 24, alt = "Green Book" }) => (
  <img src="/assets/GreenBook.png" width={size} height={size} alt={alt} />
);
const PurpleCalendarIcon = ({ size = 24, alt = "Purple Calendar" }) => (
  <img src="/assets/purpleCalendar.png" width={size} height={size} alt={alt} />
);

// New icons based on the user's images
const NexorahAIIcon = ({ size = 24, alt = "Nexorah AI Icon" }) => (
  <img src="/assets/nexorahAIIcon.png" width={size} height={size} alt={alt} />
);

const ExclamationIcon = ({ size = 20, color = "#e24d2b", alt = "Exclamation Icon" }) => (
  <div style={{ marginRight: '8px', marginTop: '8px' }}>
    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 12.5C0 5.87258 5.37258 0.5 12 0.5C18.6274 0.5 24 5.87258 24 12.5C24 19.1274 18.6274 24.5 12 24.5C5.37258 24.5 0 19.1274 0 12.5Z" fill="#E74C3C" />
      <g clip-path="url(#clip0_234_4998)">
        <path d="M12 17.1666V17.1733M12 14.5V7.83331" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_234_4998">
          <rect width="16" height="16" fill="white" transform="translate(4 4.5)" />
        </clipPath>
      </defs>
    </svg>

  </div>
);



// Chart SVG component
const ChartSVG = ({ color, value, size = "default" }) => {
  const percentage = Math.max(
    0,
    Math.min(100, Number(String(value).replace("%", "")) || 0)
  );

  const width = size === "small" ? 60 : 82;
  const height = size === "small" ? 30 : 43;

  const y = height - (percentage / 100) * (height - 8);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
    >
      <defs>
        <linearGradient
          id={`gradient-${color.replace("#", "")}`}
          x1="0"
          y1="0"
          x2="0"
          y2={height}
        >
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      <path
        d={`
          M 0 ${height}
          C 15 ${height - 4},
            25 ${y + 8},
            40 ${y}
          S 65 ${y - 4},
            ${width} ${y}
          L ${width} ${height}
          Z
        `}
        fill={`url(#gradient-${color.replace("#", "")})`}
      />

      <path
        d={`
          M 0 ${height - 4}
          C 15 ${height - 4},
            25 ${y + 8},
            40 ${y}
          S 65 ${y - 4},
            ${width} ${y}
        `}
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
};

// Same sm/md thresholds as the shared Sidebar/Navbar shell (768/992), plus
// an extra "xl" tier above 1200 for wide-desktop-only refinements - that
// extra tier doesn't desync with anything since the shell doesn't change
// again above 992 either way.
const getBreakpoint = (width) => {
  if (width < BREAKPOINTS.mobile) return "sm";
  if (width < BREAKPOINTS.tablet) return "md";
  if (width < 1200) return "lg";
  return "xl";
};

export default function Dashboard() {
  const moduleNumOf = (area) => Number(String(area.module_id || "").replace("M", "")) || 0;
const chapterNumOf = (area) => {
  const match = /Chapter\s+(\d+)/i.exec(area.chapter_title || "");
  return Number(match?.[1] ?? area.chapter_number ?? 0);
};
  const { width } = useResponsive();
  const breakpoint = getBreakpoint(width);
  const isMobile = breakpoint === "sm";
  const isTablet = breakpoint === "md";

  useEffect(() => {
    if (!document.getElementById("dashboard-responsive-styles")) {
      const style = document.createElement("style");
      style.id = "dashboard-responsive-styles";
      style.innerHTML = `
      .dashboard-main {
        margin: 0;
        padding: 0;
        width: 100%;
        min-height: 100vh;
        background: #ECF2FE;
        font-family: -apple-system, BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
      }
      .dashboard-content {
        max-width: 1400px;
        margin: 0 auto;
        padding: 24px 32px;
      }
      .dashboard-stat-cards {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 24px;
        margin-bottom: 24px;
      }
      .dashboard-recs-grid {
        display: grid;
        grid-template-columns: 1fr; /* FIXED: Always one column */
        gap: 24px;
      }
      @media (max-width: 1200px) {
        .dashboard-content { padding: 16px; }
        .dashboard-stat-cards { gap: 16px; }
        .dashboard-recs-grid { gap: 16px; }
      }
      @media (max-width: 900px) {
        .dashboard-content { padding: 16px; }
        .dashboard-stat-cards { grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .dashboard-recs-grid { grid-template-columns: 1fr; gap: 16px; }
      }
      @media (max-width: 640px) {
        body, .dashboard-main {
          overflow-x: hidden;
        }
        .dashboard-content {
          padding: 16px; /* Adjusted for better mobile spacing */
        }
        .dashboard-stat-cards { grid-template-columns: 1fr; gap: 12px; margin-bottom: 16px;}
        .dashboard-recs-grid { grid-template-columns: 1fr; gap: 12px;}
      }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const buttonStyles = {
    padding: isMobile ? "8px 12px" : "10px 16px",
    borderRadius: "8px",
    border: "none",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: isMobile ? "0.8rem" : "0.9rem",
    transition: "all 0.3s ease",
    textTransform: "none",
  };

  const cardStyles = {
    background: "#FFFFFF",
    borderRadius: isMobile ? "12px" : isTablet ? "16px" : "18px",
    boxShadow: "0px 4px 16px 0px rgba(35,57,93,0.04)",
    padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
    minHeight: isMobile ? "100px" : isTablet ? "120px" : "135px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: isMobile ? "8px" : "12px",
    position: "relative",
    transition: "all 0.3s ease",
    cursor: "pointer",
  };

  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboard);

  const stats = dashboardData?.stats;
  const dashboardReady = Boolean(stats);

  const preAssessment = stats?.pre_assessment || {};
  const coverageValue = Number(stats?.readiness_coverage || 0);
  const firstBlocker = (stats?.readiness_blockers && stats.readiness_blockers[0]) || null;
  const sortedWeakAreas = [...(stats?.weak_areas || [])].sort(
    (a, b) => moduleNumOf(a) - moduleNumOf(b) || chapterNumOf(a) - chapterNumOf(b)
  );
  const cardData = dashboardReady ? [
    {
      label: "Pre-Assessment",
      value: preAssessment.score ?? stats.pre_assessment_readiness ?? 0,
      display: `${preAssessment.score ?? stats.pre_assessment_readiness ?? 0}%`,
      icon: ReadyIcon,
      iconBg: "#152E51",
      chartColor: "#152E51",
      detail: preAssessment.badge || null,
      note: preAssessment.message || null,
      tone: preAssessment.tone || null,
    },
    {
      label: "Course Completion",
      value: stats.course_completion_percent ?? stats.overall_progress_percent ?? 0,
      display: `${stats.course_completion_percent ?? stats.overall_progress_percent ?? 0}%`,
      icon: DashboardIcon,
      iconBg: "#152E51",
      chartColor: "#152E51",
      detail: `${stats.chapters_finished ?? 0} of ${stats.chapters_total ?? 63} chapters`,
    },
    {
      label: "Exam Readiness",
      value: stats.readiness_score || 0,
      display: `${stats.readiness_score || 0}%`,
      icon: BookIcon,
      iconBg: "#22C899",
      chartColor: "#22C899",
      detail: stats.readiness_provisional
        ? "Provisional"
        : (stats.readiness_ready ? "Ready" : "Not ready yet"),
      note: firstBlocker,
    },
    {
      label: "Study Streak",
      value: Math.min(stats.study_streak_days || 0, 100),
      display: `${stats.study_streak_days || 0}`,
      icon: CalendarIcon,
      iconBg: "#152E51",
      chartColor: "#6F7AE8",
      sub: "days in a row",
    },
    {
      label: "This Week",
      value: Math.min(((stats.hours_this_week || 0) / (stats.weekly_goal_hours || 15)) * 100, 100),
      display: `${stats.hours_this_week || 0}h`,
      icon: TrophyIcon,
      iconBg: "#F2994A",
      chartColor: "#F2994A",
      sub: `/${stats.weekly_goal_hours || 15}h`,
    },
  ] : [];


  useEffect(() => {
    dispatch(fetchDashboardData());
  }, [dispatch]);



  const navigate = useNavigate();

  if (!dashboardReady) {
    return (
      <div className="dashboard-main">
        <div
          className="dashboard-content"
          style={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#5A6A85",
            fontSize: "16px",
          }}
        >
          {dashboardData?.error ? "Unable to load your dashboard." : "Loading your dashboard..."}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-main">
      <div className="dashboard-content">
        {/* Stat Cards */}
        <div className="dashboard-stat-cards">
          {cardData.map((card, idx) => {
            const IconComponent = card.icon;
            return (
              <div
                key={card.label}
                style={{
                  ...cardStyles,
                  transform: "translateY(0px)",
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0px 8px 24px 0px rgba(35,57,93,0.08)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = "translateY(0px)";
                    e.currentTarget.style.boxShadow =
                      "0px 4px 16px 0px rgba(35,57,93,0.04)";
                  }
                }}
              >
                {/* Top Section (Value and Label) */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      color: "#152E51",
                      fontSize: isMobile ? "1.1rem" : isTablet ? "1.4rem" : "1.6rem",
                      fontWeight: "bold",
                      lineHeight: 1.1,
                    }}
                  >
                    {card.display}
                    {card.sub && (
                      <span
                        style={{
                          fontWeight: "500",
                          color: "#899bbd",
                          fontSize: isMobile ? "0.7rem" : "1rem",
                          marginLeft: "4px",
                          position: "relative",
                          bottom: "2px",
                          display: "inline",
                        }}
                      >
                        {card.sub}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      color: "#23395d",
                      fontWeight: "500",
                      fontSize: isMobile ? "0.85rem" : isTablet ? "0.95rem" : "1rem",
                      lineHeight: 1.1,
                    }}
                  >
                    {card.label}
                  </div>
                  {card.detail && (
                    <div
                      style={{
                        marginTop: "6px",
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "3px 8px",
                        borderRadius: "999px",
                        fontSize: isMobile ? "0.68rem" : "0.75rem",
                        fontWeight: 700,
                        lineHeight: 1.2,
                        backgroundColor:
                          card.tone === "good"
                            ? "#ECFDF3"
                            : card.tone === "ready_with_prep"
                              ? "#FFF7ED"
                              : card.tone === "build_foundations"
                                ? "#FEF3F2"
                                : card.detail === "Provisional"
                                  ? "#EEF4FF"
                                  : card.detail === "Ready"
                                    ? "#ECFDF3"
                                    : "#F5F7FB",
                        color:
                          card.tone === "good"
                            ? "#027A48"
                            : card.tone === "ready_with_prep"
                              ? "#B54708"
                              : card.tone === "build_foundations"
                                ? "#B42318"
                                : card.detail === "Ready"
                                  ? "#027A48"
                                  : "#364A7A",
                      }}
                    >
                      {card.detail}
                    </div>
                  )}
                  {card.note && (
                    <div
                      style={{
                        marginTop: "6px",
                        color: "#6B7C93",
                        fontSize: isMobile ? "0.68rem" : "0.75rem",
                        lineHeight: 1.35,
                        fontWeight: 500,
                      }}
                    >
                      {card.note}
                    </div>
                  )}
                </div>

                {/* Bottom Section (Icon and Chart) */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    width: "100%",
                  }}
                >
                  {/* Icon Button */}
                  <div
                    style={{
                      width: isMobile ? "36px" : isTablet ? "40px" : "44px",
                      height: isMobile ? "36px" : isTablet ? "40px" : "44px",
                      backgroundColor: card.iconBg,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      flexShrink: 0,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      transition: "transform 0.2s ease-in-out",
                    }}
                    onMouseEnter={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = "scale(1.05)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = "scale(1)";
                      }
                    }}
                  >
                    <IconComponent
                      size={isMobile ? 18 : isTablet ? 22 : 26}
                      color="white"
                    />
                  </div>
                  {/* Chart SVG */}
                  <div>
                    <ChartSVG
                      color={card.chartColor}
                      value={card.value}
                      size={isMobile ? "small" : "default"}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Smart Focus Section */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: isMobile ? "12px" : isTablet ? "16px" : "18px",
            boxShadow: "0px 8px 32px 0px rgba(35, 57, 93, 0.07)",
            padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
            marginBottom: isMobile ? "16px" : "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              marginBottom: isMobile ? "16px" : "24px",
              gap: isMobile ? "12px" : "0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                fontWeight: "700",
                color: "#0F141F",
                fontSize: isMobile ? "1rem" : isTablet ? "1.05rem" : "1.1rem",
              }}
            >
              <div style={{ marginRight: '8px' }}>
                <NexorahAIIcon size={isMobile ? 24 : 28} />
              </div>
              Smart Focus by Nexorah AI
            </div>
            <button
              style={{
                ...buttonStyles,
                backgroundColor: "#3DA9FC",
                color: "#ffff",
                borderRadius: "50px",
                fontWeight: "500",
                alignSelf: isMobile ? "flex-start" : "auto",
              }}
            >
              AI POWERED
            </button>
          </div>
          {/* Info Alert */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: isMobile ? "8px" : isTablet ? "10px" : "12px",
              boxShadow: "0px 2px 12px 0px rgba(35, 57, 93, 0.07)",
              padding: isMobile ? "12px" : isTablet ? "16px" : "20px",
              marginBottom: isMobile ? "12px" : "16px",
              borderLeft: (isMobile ? "4px" : "5px") + " solid #e24d2b",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                color: "#374151",
                fontWeight: "400",
                fontSize: isMobile ? "0.85rem" : isTablet ? "0.95rem" : "1.08rem",
                lineHeight: 1.9,
                width: "100%",
                maxWidth: "620px",
              }}
            >
              <ExclamationIcon size={isMobile ? 18 : 20} color="#e24d2b" />
              {sortedWeakAreas.length > 0
                ? `You have ${sortedWeakAreas.length} chapter${sortedWeakAreas.length > 1 ? "s" : ""} below 80%. Tap a chapter below and retake its quiz to pass it.`
                : "Complete assessments to get personalized Smart Focus recommendations here."}
            </div>
          </div>
          {/* Weakest Area Card */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: isMobile ? "8px" : isTablet ? "10px" : "12px",
              boxShadow: "0px 2px 12px 0px rgba(35, 57, 93, 0.07)",
              padding: isMobile ? "12px" : isTablet ? "16px" : "20px",
              marginBottom: isMobile ? "12px" : "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: isMobile ? "8px" : "12px",
                flexDirection: isMobile ? "column" : "row",
                textAlign: isMobile ? "center" : "left",
                gap: isMobile ? "8px" : "0",
              }}
            >
              <div style={{ marginRight: isMobile ? "0" : "16px" }}>
                <div
                  style={{
                    width: isMobile ? "36px" : isTablet ? "40px" : "44px",
                    height: isMobile ? "36px" : isTablet ? "40px" : "44px",
                    backgroundColor: "#e24d2b",
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <WarningTriangleIcon size={isMobile ? 20 : isTablet ? 24 : 28} color="#fff" />
                </div>
              </div>
              <div>
                <div
                  style={{
                    color: "#e24d2b",
                    fontWeight: "bold",
                    fontSize: isMobile ? "0.9rem" : isTablet ? "1rem" : "1.09rem",
                  }}
                >
                  Weakest Areas
                </div>
              </div>
            </div>

            {/* All attempted chapters below 80%, click to retake the quiz */}
            {sortedWeakAreas.length > 0 ?  (
              sortedWeakAreas.map((area, index) => (
                <div
                  key={area.chapter_id}
                  onClick={() =>
                    navigate("/resources", { state: { openChapterId: area.chapter_id } })
                  }
                  style={{
                    marginTop: isMobile ? "12px" : "14px",
                    paddingTop: index === 0 ? 0 : (isMobile ? "12px" : "14px"),
                    borderTop: index === 0 ? "none" : "1px solid #eef1f6",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
                    <div>
                      <div
                        style={{
                          color: "#899bbd",
                          fontWeight: 600,
                          fontSize: isMobile ? "0.68rem" : "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                       {`Module ${moduleNumOf(area)} · Chapter ${chapterNumOf(area)}`} 
                      </div>
                      <div
                        style={{
                          color: "#23395d",
                          fontWeight: "500",
                          fontSize: isMobile ? "0.85rem" : "0.95rem",
                        }}
                      >
                       {(area.chapter_title || "").replace(/^Chapter\s+\d+\s*:\s*/i, "")}
                      </div>
                    </div>
                    <span style={{ color: "#e24d2b", fontWeight: 700, fontSize: isMobile ? "0.8rem" : "0.9rem", whiteSpace: "nowrap" }}>
                      {area.score_percent}%
                    </span>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: isMobile ? "6px" : "8px",
                      backgroundColor: "#e5e8f0",
                      borderRadius: "6px",
                      marginTop: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: `${area.score_percent}%`,
                        height: "100%",
                        backgroundColor: "#ffc82c",
                        borderRadius: "6px",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: "#899bbd", fontSize: isMobile ? "0.85rem" : "0.95rem", marginTop: "8px" }}>
                {stats.strongest_topic
                  ? "Nice work — no weak modules right now."
                  : "Complete assessments to see weak modules here."}
              </div>
            )}
          </div>

          {/* Suggested Action */}
          <div
            style={{
              backgroundColor: "#edf7fa",
              borderRadius: isMobile ? "8px" : isTablet ? "10px" : "12px",
              boxShadow: "0px 2px 12px 0px rgba(35, 57, 93, 0.07)",
              padding: isMobile ? "12px" : isTablet ? "16px" : "20px",
              marginBottom: isMobile ? "12px" : "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: isMobile ? "column" : "row",
                textAlign: isMobile ? "center" : "left",
                gap: isMobile ? "8px" : "0",
                marginBottom: isMobile ? "12px" : "16px", // Added margin bottom to separate from nested card
              }}
            >
              <div style={{ marginRight: isMobile ? "0" : "16px" }}>
                <div
                  style={{
                    width: isMobile ? "32px" : isTablet ? "34px" : "36px",
                    height: isMobile ? "32px" : isTablet ? "34px" : "36px",
                    backgroundColor: "#4ECDC4",
                    border: "2px solid #4ECDC4",
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    boxShadow: "0 1px 8px rgba(34,156,113,0.12)",
                    marginTop: "-25px",
                  }}
                >
                  <GreenBookIcon size={isMobile ? 16 : isTablet ? 20 : 24} color="#229c71" />
                </div>
              </div>
              <div>
                <div
                  style={{
                    color: "#1F2937",
                    fontWeight: "bold",
                    fontSize: isMobile ? "0.9rem" : isTablet ? "1rem" : "1.09rem",
                    paddingBottom: "20px",
                  }}
                >
                  Suggested Action
                </div>
                <div
                  style={{
                    color: "#374151",
                    fontWeight: "400",
                    fontSize: isMobile ? "0.85rem" : isTablet ? "0.95rem" : "1rem",
                    lineHeight: 1.3,
                  }}
                >
                  Study this topic 2 hours per week to improve your foundation
                </div>
              </div>
            </div>

            {/* Recommended Schedule (nested card) */}
            <div
              style={{
                backgroundColor: "#fff", // White background for the nested card
                borderRadius: isMobile ? "8px" : isTablet ? "10px" : "12px",
                boxShadow: "0px 2px 12px 0px rgba(35, 57, 93, 0.07)", // Shadow for the nested card
                padding: isMobile ? "12px" : isTablet ? "16px" : "20px",
                marginTop: isMobile ? "12px" : "0px", // Adjusted margin
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: isMobile ? "column" : "row",
                  textAlign: isMobile ? "center" : "left",
                  gap: isMobile ? "8px" : "0",
                }}
              >
                <div style={{ marginRight: isMobile ? "0" : "16px" }}>
                  <div
                    style={{
                      width: isMobile ? "32px" : isTablet ? "34px" : "36px",
                      height: isMobile ? "32px" : isTablet ? "34px" : "36px",
                      backgroundColor: "#8B5FBF",
                      border: "2px solid #8B5FBF",
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      boxShadow: "0 1px 8px rgba(132,89,236,0.12)",
                    }}
                  >
                    <PurpleCalendarIcon
                      size={isMobile ? 16 : isTablet ? 20 : 24}
                      color="#8B5FBF"
                    />
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      color: "#1F2937",
                      fontWeight: "bold",
                      fontSize: isMobile ? "0.9rem" : isTablet ? "1rem" : "1.09rem",
                    }}
                  >
                    Recommended Study Schedule
                  </div>
                  <div
                    style={{
                      color: "#4B5563",
                      fontWeight: "400",
                      fontSize: isMobile ? "0.85rem" : isTablet ? "0.95rem" : "1rem",
                      lineHeight: 1.3,
                    }}
                  >
                    30 minutes daily × 4 days = Optimal learning retention
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Recommendations Section Container */}
        <div
          style={{
            backgroundColor: "#fff", // Overall white background
            borderRadius: isMobile ? "12px" : isTablet ? "16px" : "18px",
            boxShadow: "0px 8px 32px 0px rgba(35, 57, 93, 0.07)",
            padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
            marginBottom: "24px",
            marginTop: isMobile ? "16px" : "24px",
          }}
        >
          <div
            style={{
              color: "#23395d",
              marginBottom: isMobile ? "12px" : "16px",
              fontSize: isMobile ? "1rem" : isTablet ? "1.1rem" : "1.15rem",
              fontWeight: "bold",
            }}
          >
            AI Recommendations
          </div>

          {/* Recommendation Cards Grid */}
          <div className="dashboard-recs-grid">
            {/* Ready for Practice Test Card */}
            <div
              style={{
                backgroundColor: "#e2eeff", // Light blue background
                borderRadius: isMobile ? "12px" : isTablet ? "16px" : "18px",
                boxShadow: "0px 8px 32px 0px rgba(35, 57, 93, 0.07)",
                padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0px 12px 40px 0px rgba(35, 57, 93, 0.12)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = "translateY(0px)";
                  e.currentTarget.style.boxShadow =
                    "0px 8px 32px 0px rgba(35, 57, 93, 0.07)";
                }
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: isMobile ? "12px" : "16px",
                  flexDirection: isMobile ? "column" : "row",
                  textAlign: isMobile ? "center" : "left",
                  gap: isMobile ? "8px" : "0",
                }}
              >
                <div
                  style={{
                    width: isMobile ? "36px" : isTablet ? "38px" : "40px",
                    height: isMobile ? "36px" : isTablet ? "38px" : "40px",
                    backgroundColor: "#23395d",
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: isMobile ? "0" : "16px",
                  }}
                >
                  <ReadyIcon
                    size={isMobile ? 18 : isTablet ? 20 : 24}
                    color="white"
                  />
                </div>
                <div
                  style={{
                    color: "#23395d",
                    fontSize: isMobile ? "0.9rem" : isTablet ? "1rem" : "1.09rem",
                    fontWeight: "bold",
                  }}
                >
                  Ready for Practice Test
                </div>
              </div>
              <div
                style={{
                  color: "#23395d",
                  marginBottom: isMobile ? "16px" : "20px",
                  fontWeight: "500",
                  fontSize: isMobile ? "0.85rem" : isTablet ? "0.95rem" : "1rem",
                  lineHeight: 1.4,
                  flexGrow: 1,
                }}
              >
                Your Quality Management skills are strong. Take a full mock exam to gauge
                overall progress.
              </div>
              <button
                onClick={() => {
                  // Navigate to the practice test page
                  navigate("/question-bank"); // Adjust the URL as needed
                }}
                style={{
                  ...buttonStyles,
                  backgroundColor: "#23395d",
                  color: "#fff",
                  width: isMobile ? "100%" : "fit-content",
                  padding: isMobile ? "12px 16px" : "12px 24px",
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.target.style.backgroundColor = "#1a2d4a";
                    e.target.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.target.style.backgroundColor = "#23395d";
                    e.target.style.transform = "translateY(0px)";
                  }
                }}
              >
                Take Test
              </button>
            </div>

            {/* Maintain Study Momentum Card */}
            <div
              style={{
                backgroundColor: "#fff6e2", // Light orange-yellow background
                borderRadius: isMobile ? "12px" : isTablet ? "16px" : "18px",
                boxShadow: "0px 8px 32px 0px rgba(35, 57, 93, 0.07)",
                padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0px 12px 40px 0px rgba(35, 57, 93, 0.12)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = "translateY(0px)";
                  e.currentTarget.style.boxShadow =
                    "0px 8px 32px 0px rgba(35, 57, 93, 0.07)";
                }
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: isMobile ? "12px" : "16px",
                  flexDirection: isMobile ? "column" : "row",
                  textAlign: isMobile ? "center" : "left",
                  gap: isMobile ? "8px" : "0",
                }}
              >
                <div
                  style={{
                    width: isMobile ? "36px" : isTablet ? "38px" : "40px",
                    height: isMobile ? "36px" : isTablet ? "38px" : "40px",
                    backgroundColor: "#f2994a",
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: isMobile ? "0" : "16px",
                  }}
                >
                  <DocumentIcon
                    size={isMobile ? 18 : isTablet ? 20 : 24}
                    color="white"
                  />
                </div>
                <div
                  style={{
                    color: "#f2994a",
                    fontSize: isMobile ? "0.9rem" : isTablet ? "1rem" : "1.09rem",
                    fontWeight: "bold",
                  }}
                >
                  Maintain Study Momentum
                </div>
              </div>
              <div
                style={{
                  color: "#23395d",
                  fontWeight: "500",
                  fontSize: isMobile ? "0.85rem" : isTablet ? "0.95rem" : "1rem",
                  lineHeight: 1.4,
                  flexGrow: 1,
                }}
              >
                Excellent 12-day streak! Keep up your daily 1-hour sessions to maintain
                consistency.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}